import { BIG_TUBE_VOLUME, MIN_BAG_ITEMS, MIN_BAR_ITEMS, MIN_BISCUIT_ITEMS, SMALL_TUBE_VOLUME } from '@/consts/mix'
import { MixNutritionService } from '@/services/mixNutritionService'
import type { IngredientItem } from '@/types/ingredient'
import type { Nutrients } from '@/types/nutrients'

export class Mix {
  name?: string
  state: string = 'new' // asi se nepoužívá
  quantity: number = 1
  items: IngredientItem[] = []
  nutrients: Nutrients = {
    fats: 0,
    saturatedFats: 0,
    carbohydrates: 0,
    sugars: 0,
    fibre: 0,
    proteins: 0,
    salt: 0,
    energy: 0,
    energyKcal: 0,
  }
  isShared?: boolean 

  constructor(public kind: string) {}

  // Defines if mix is empty or not
  isEmpty() {
    return this.items?.length === 0
  }

  // Calculates nutrients.
  calculateNutrients() {
    const service = new MixNutritionService(this)
    service.calculate()
  }

  incrementQuantity() {
    this.quantity++
  }

  decrementQuantity() {
    if (this.quantity > 1) this.quantity--
  }

  serializeMix() {
    return {
      kind: this.kind,
      name: this.name,
      items: this.items.map((i) => {
        return {
          id: i.ingredient.id,
          quantity: i.amount,
        }
      }),
    }
  }

  // Calculates total non-base quantity.
  get totalNonBaseItemsQuantity() {
    return this.nonBaseItems.map((item) => item.amount).reduce((sum, amount) => sum + amount, 0)
  }

  // Defines if mix should has base or not.
  get shouldHasBase() {
    return this.kind === 'musli' || this.kind === 'porridge'
  }

  get hasBase() {
    return this.baseItems.length > 0
  }

  get totalWeight() {
    let totalWeight = 0
    for (const item of this.nonBaseItems) {
      totalWeight += item.ingredient.weight * item.amount
    }
    return this.base ? this.base.ingredient.weight * (this.basePercent / 100) + totalWeight : totalWeight
  }

  get totalPrice() {
    let totalPrice = 0
    for (const item of this.items) {
      totalPrice += Number.parseFloat(item.ingredient.price) * item.amount
    }
    return totalPrice
  }

  get pricePerPortion() {
    if (this.totalPrice > 0) {
      return this.totalPrice / (this.totalWeight / 100)
    }
    return 0
  }

  get baseItems() {
    return this.items.filter((item) => item.ingredient.isBase)
  }

  get nonBaseItems() {
    return this.items.filter((item) => !item.ingredient.isBase)
  }

  get baseExtendedItems() {
    return this.items.filter((item) => item.ingredient.parentCategoryId === 'musli_base_extended')
  }

  get dryFruitsItems() {
    return this.items.filter(
      (item) =>
        item.ingredient.parentCategoryId === 'dry_fruits' || item.ingredient.parentCategoryId === 'crunchy_fruits',
    )
  }

  get nutsItems() {
    return this.items.filter((item) => item.ingredient.parentCategoryId === 'nuts')
  }

  get onTopItems() {
    return this.items.filter((item) => item.ingredient.parentCategoryId === 'on_top')
  }

  get base() {
    return this.baseItems[0]
  }

  get tubeVolume() {
    if (this.kind === 'musli' || this.kind === 'porridge') {
      return BIG_TUBE_VOLUME
    }
    return SMALL_TUBE_VOLUME
  }

  get nonBaseItemsVolume() {
    return this.nonBaseItems
      .map((item) => Number.parseFloat(item.ingredient.constantGml) * item.ingredient.weight * item.amount)
      .reduce((sum, ml) => sum + ml, 0)
  }

  get baseExtendedItemsVolume() {
    return this.baseExtendedItems
      .map((item) => Number.parseFloat(item.ingredient.constantGml) * item.ingredient.weight * item.amount)
      .reduce((sum, ml) => sum + ml, 0)
  }

  get dryFruitItemsVolume() {
    return this.dryFruitsItems
      .map((item) => Number.parseFloat(item.ingredient.constantGml) * item.ingredient.weight * item.amount)
      .reduce((sum, ml) => sum + ml, 0)
  }

  get nutItemsItemsVolume() {
    return this.nutsItems
      .map((item) => Number.parseFloat(item.ingredient.constantGml) * item.ingredient.weight * item.amount)
      .reduce((sum, ml) => sum + ml, 0)
  }

  get onTopItemsVolume() {
    return this.onTopItems
      .map((item) => Number.parseFloat(item.ingredient.constantGml) * item.ingredient.weight * item.amount)
      .reduce((sum, ml) => sum + ml, 0)
  }

  get baseExtendedItemsPercent() {
    return (this.baseExtendedItemsVolume / this.tubeVolume) * 100
  }

  get dryFruitItemsPercent() {
    return (this.dryFruitItemsVolume / this.tubeVolume) * 100
  }

  get nutItemsPercent() {
    return (this.nutItemsItemsVolume / this.tubeVolume) * 100
  }

  get onTopItemsPercent() {
    return (this.onTopItemsVolume / this.tubeVolume) * 100
  }

  get basePercent() {
    if (this.hasBase) {
      return 100 - this.itemsPercent
    }
    return 0
  }

  get itemsPercent() {
    switch (this.kind) {
      case 'musli':
      case 'porridge': {
        return this.baseExtendedItemsPercent + this.dryFruitItemsPercent + this.nutItemsPercent + this.onTopItemsPercent
      }
      case 'dry_fruits':
      case 'crunchy_fruits': {
        return this.dryFruitItemsPercent + this.nutItemsPercent + this.onTopItemsPercent
      }
      case 'biscuits': {
        return (this.totalNonBaseItemsQuantity * 100) / MIN_BISCUIT_ITEMS
      }
      case 'fruit_bags': {
        return (this.totalNonBaseItemsQuantity * 100) / MIN_BAG_ITEMS
      }
      case 'mixit_bars': {
        return (this.totalNonBaseItemsQuantity * 100) / MIN_BAR_ITEMS
      }
      // No default
    }
    return 0
  }

  get showBasePercent() {
    return this.kind === 'musli' || this.kind === 'porridge'
  }

  get showBaseExtendedItemsPercent() {
    return this.kind === 'musli'
  }

  get showDryFruitItemsPercent() {
    return this.kind === 'musli' || this.kind === 'porridge' || this.kind === 'dry_fruits'
  }

  get showNutItemsPercent() {
    return this.kind === 'musli' || this.kind === 'porridge' || this.kind === 'dry_fruits'
  }

  get showOnTopItemsPercent() {
    return this.kind === 'musli' || this.kind === 'porridge' || this.kind === 'dry_fruits'
  }

  get showNutritionTable() {
    return (
      this.kind === 'musli' ||
      this.kind === 'porridge' ||
      this.kind === 'dry_fruits' ||
      this.kind === 'crunchy_fruits' ||
      this.kind === 'biscuits'
    )
  }
}
